/**
 * VEDHACART Delivery Zone Validation
 * Reads zone configuration from window.VEDHACART_EATS (set by theme)
 * FREE delivery on orders above threshold
 */

(function() {
  'use strict';

  // Get config from theme (with fallbacks)
  function getConfig() {
    return window.VEDHACART_EATS || window.EATS_MENU_CONFIG || {
      freeDeliveryThreshold: 29,
      deliveryArea: '20 miles from Nottingham',
      deliveryZones: []
    };
  }

  // UK Postcode regex
  const UK_POSTCODE_REGEX = /^([A-Z]{1,2}[0-9][0-9A-Z]?\s?[0-9][A-Z]{2})$/i;

  /**
   * Extract outward code (prefix) from postcode
   * e.g., "NG2 3JJ" -> "NG2", "DE72 3AB" -> "DE72"
   */
  function getOutwardCode(postcode) {
    const clean = postcode.toUpperCase().replace(/\s+/g, '').trim();
    // Match the outward code (everything before the last 3 characters)
    if (clean.length >= 5) {
      return clean.slice(0, -3).trim();
    }
    return clean;
  }

  /**
   * Find zone for a given postcode
   */
  function findZone(postcode) {
    const config = getConfig();
    const outward = getOutwardCode(postcode);
    
    for (const zone of config.deliveryZones) {
      if (zone.postcodes && zone.postcodes.includes(outward)) {
        return zone;
      }
    }
    
    return null;
  }

  /**
   * Validate and clean postcode
   */
  function cleanPostcode(postcode) {
    return postcode.toUpperCase().replace(/\s+/g, '').trim();
  }

  function formatPostcode(postcode) {
    const clean = cleanPostcode(postcode);
    // Insert space before last 3 characters
    if (clean.length > 3) {
      return clean.slice(0, -3) + ' ' + clean.slice(-3);
    }
    return clean;
  }

  /**
   * Check if postcode is in delivery zone
   * Returns: { valid: boolean, zone: object|null, message: string }
   */
  async function checkDeliveryZone(postcode) {
    const config = getConfig();
    const clean = cleanPostcode(postcode);

    // Basic validation
    if (!UK_POSTCODE_REGEX.test(clean)) {
      return {
        valid: false,
        zone: null,
        message: 'Please enter a valid UK postcode'
      };
    }

    // Validate postcode exists using postcodes.io (optional but recommended)
    try {
      const response = await fetch('https://api.postcodes.io/postcodes/' + encodeURIComponent(clean) + '/validate');
      const data = await response.json();
      
      if (!data.result) {
        return {
          valid: false,
          zone: null,
          message: 'Postcode not found. Please check and try again.'
        };
      }
    } catch (error) {
      // If API fails, continue with zone check
      console.warn('Postcode validation API unavailable:', error);
    }

    // Find zone
    const zone = findZone(clean);
    
    if (!zone) {
      const deliveryArea = config.deliveryArea || '20 miles of Nottingham';
      return {
        valid: false,
        zone: null,
        message: "Sorry, we don't deliver to " + formatPostcode(postcode) + ". We deliver within " + deliveryArea + "."
      };
    }

    // Get cart total to check for free delivery
    const freeThreshold = (config.freeDeliveryThreshold || 29) * 100; // Convert to pence
    let cartTotal = 0;
    let freeDelivery = false;
    
    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      cartTotal = cart.total_price;
      freeDelivery = cartTotal >= freeThreshold;
    } catch (e) {
      console.warn('Could not fetch cart total:', e);
    }

    // Build message
    let message;
    const rate = zone.rate || (zone.charge / 100).toFixed(2);
    
    if (freeDelivery) {
      message = "Great news! FREE delivery to " + formatPostcode(postcode) + " on your order!";
    } else {
      const amountNeeded = ((freeThreshold - cartTotal) / 100).toFixed(2);
      message = "We deliver to " + formatPostcode(postcode) + "! Delivery: £" + rate + ". Spend £" + amountNeeded + " more for FREE delivery.";
    }

    return {
      valid: true,
      zone: {
        key: zone.key,
        name: zone.name,
        charge: freeDelivery ? 0 : zone.charge,
        originalCharge: zone.charge,
        rate: rate,
        label: freeDelivery ? 'FREE delivery' : '£' + rate + ' delivery',
        freeDelivery: freeDelivery
      },
      message: message
    };
  }

  /**
   * Initialize the postcode checker UI
   */
  function initZoneChecker() {
    const input = document.getElementById('postcode-input');
    const button = document.getElementById('check-postcode-btn');
    const resultEl = document.getElementById('zone-result');

    if (!input || !button || !resultEl) return;

    async function doCheck() {
      const postcode = input.value.trim();
      
      if (!postcode) {
        showResult(resultEl, {
          status: 'error',
          icon: '⚠️',
          message: 'Please enter your postcode'
        });
        return;
      }

      // Show loading
      button.disabled = true;
      button.textContent = 'Checking...';
      resultEl.hidden = true;

      const result = await checkDeliveryZone(postcode);

      // Show result
      if (result.valid) {
        showResult(resultEl, {
          status: 'success',
          icon: '✓',
          message: result.message
        });

        // Store for checkout
        try {
          localStorage.setItem('vc_delivery_postcode', formatPostcode(postcode));
          localStorage.setItem('vc_delivery_zone', JSON.stringify(result.zone));
        } catch (e) {}

        // Dispatch event for other components
        document.dispatchEvent(new CustomEvent('vc:delivery:validated', {
          detail: {
            postcode: formatPostcode(postcode),
            zone: result.zone
          }
        }));

      } else {
        showResult(resultEl, {
          status: 'error',
          icon: '✗',
          message: result.message
        });
      }

      button.disabled = false;
      button.textContent = 'Check';
    }

    // Button click
    button.addEventListener('click', doCheck);

    // Enter key
    input.addEventListener('keypress', function(e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        doCheck();
      }
    });

    // Auto-format on blur
    input.addEventListener('blur', function() {
      if (input.value.trim()) {
        input.value = formatPostcode(input.value);
      }
    });

    // Load saved postcode if exists
    try {
      const savedPostcode = localStorage.getItem('vc_delivery_postcode');
      if (savedPostcode) {
        input.value = savedPostcode;
      }
    } catch (e) {}
  }

  function showResult(el, opts) {
    el.hidden = false;
    el.dataset.status = opts.status;
    el.innerHTML = '<span class="zone-result__icon">' + opts.icon + '</span><span class="zone-result__text">' + opts.message + '</span>';
  }

  /**
   * Delivery Zone API for other components
   */
  window.DeliveryZone = {
    check: checkDeliveryZone,
    
    getConfig: getConfig,
    
    getZones: function() { return getConfig().deliveryZones; },
    
    getFreeThreshold: function() { return (getConfig().freeDeliveryThreshold || 29) * 100; },
    
    findZone: findZone,
    
    formatPostcode: formatPostcode,

    getAllPostcodes: function() {
      var config = getConfig();
      var all = [];
      for (var i = 0; i < config.deliveryZones.length; i++) {
        var zone = config.deliveryZones[i];
        if (zone.postcodes) {
          all = all.concat(zone.postcodes);
        }
      }
      return all;
    },
    
    getSavedZone: function() {
      try {
        var zone = localStorage.getItem('vc_delivery_zone');
        return zone ? JSON.parse(zone) : null;
      } catch (e) {
        return null;
      }
    },
    
    getSavedPostcode: function() {
      try {
        return localStorage.getItem('vc_delivery_postcode') || null;
      } catch (e) {
        return null;
      }
    },
    
    clearSaved: function() {
      try {
        localStorage.removeItem('vc_delivery_postcode');
        localStorage.removeItem('vc_delivery_zone');
      } catch (e) {}
    }
  };

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initZoneChecker);
  } else {
    initZoneChecker();
  }

})();
